import { useEffect, useState } from "react";
import { getWasteTrajectory, type WasteTrajectory } from "@/lib/manufacturing/wasteWalkClient";
import { ghostBtnClass } from "./manufacturingSurfaceClasses";

const WINDOWS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "month", label: "This month" },
] as const;

function usd(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function WasteWalkTrajectory({ plantId, refreshKey = 0 }: { plantId: string; refreshKey?: number }) {
  const [window, setWindow] = useState<(typeof WINDOWS)[number]["id"]>("today");
  const [report, setReport] = useState<WasteTrajectory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!plantId) return;
    let cancelled = false;
    void getWasteTrajectory(plantId, window).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setReport(null);
        setError(res.error);
        return;
      }
      setReport(res.data);
      setError(null);
    });
    return () => {
      cancelled = true;
    };
  }, [plantId, window, refreshKey]);

  return (
    <section className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Waste trajectory</h3>
        <div className="flex gap-1">
          {WINDOWS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={ghostBtnClass}
              aria-pressed={window === item.id}
              onClick={() => setWindow(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Walked items only. This sits beside the period-close waste number and does not feed it.
      </p>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {report ? (
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">In waste areas</dt>
            <dd className="font-medium text-gray-900 dark:text-zinc-100">{usd(report.in_waste_areas_usd)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Salvaged</dt>
            <dd className="font-medium text-gray-900 dark:text-zinc-100">{usd(report.salvage_usd)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">True waste</dt>
            <dd className="font-medium text-gray-900 dark:text-zinc-100">{usd(report.scrap_usd)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Packed-audit write-offs</dt>
            <dd className="font-medium text-gray-900 dark:text-zinc-100">{usd(report.packed_write_off_usd)}</dd>
          </div>
        </dl>
      ) : null}
    </section>
  );
}
